import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, useIntl, useSelector } from 'umi';
import { Bubble, Input, Radio, Select, notification } from 'tea-component';
import { find, forEach, get, map, some } from 'lodash-es';
import type {
  IConfig,
  ISearchRef,
} from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import {
  useFilterFolder,
  useListUserLevel,
} from '@/pages/dashboard/components/LeftMenu/components/hooks';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController';
import { getLeftMenuList } from '@/pages/dashboard/services/dashboard';
import { addMenu } from '@/pages/dashboard/services/leftMenu';
import { PAGE_TYPE_AUTH_PATH } from '@/pages/dashboard/_config/constant';
import {
  flattenAdminGames,
  parseMenuTree,
  type IMenuNode,
} from './utils';

/** 发布目标：经分 / 舆情字段相同、接口不同；情报暂未开放 */
type TPublishTarget = 'dashboard' | 'opinion';

/** UseFormSearch 提交值 */
type TFormValues = {
  target: TPublishTarget;
  nameEN: string;
  nameCN: string;
  gameCode: string;
  menu: string;
  folder: string;
  subFolder?: string;
  level: string;
};

type TSelectOption = { value: string; text: string; des?: string; };

/** 挂载表单 Hook 入参 */
export type IUseHtmlPublishFormOptions = {
  /** HTML 报告相对路径（/as/report/...） */
  htmlPath: string;
  /** 发布成功后关闭弹窗 */
  onSuccess: () => void;
  /** UseFormSearch ref，级联时清空下游字段 */
  formRef: React.RefObject<ISearchRef>;
};

/** useRequestController 用引用相等判断 refreshDeps，不能写在 render 里 */
const USER_MESSAGE_BODY = { style_version: 'v2' };

const EMPTY_TREE = {
  menuList: [] as IMenuNode[],
  folderList: [] as IMenuNode[],
  subFolderList: [] as IMenuNode[],
};

/** 下拉：option 带 des 时 Bubble 包在 Select 内（否则 final-form 打不到 Select） */
const FormSelect: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent) => void;
  options: TSelectOption[];
  onPicked?: (value: string) => void;
  disabled?: boolean;
  searchable?: boolean;
}> = ({ value, onChange, onBlur, options, onPicked, disabled, searchable }) => {
  const { formatMessage } = useIntl();
  const select = (
    <Select
      size="full"
      matchButtonWidth
      appearance="button"
      disabled={disabled}
      searchable={searchable}
      options={options}
      value={value}
      onBlur={onBlur}
      onChange={(next) => {
        onChange?.(next);
        onPicked?.(next);
      }}
      placeholder={formatMessage({ id: 'common_select_placeholder' })}
    />
  );
  if (!some(options, (item) => Boolean(item.des))) {
    return select;
  }
  return (
    <Bubble
      dark
      placement="right"
      trigger="click"
      content={
        <div>
          {map(options, (item) => (
            <p key={item.text}>
              ·{item.text}({item.des})
            </p>
          ))}
        </div>
      }
    >
      {select}
    </Bubble>
  );
};

const FormInput: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onPicked?: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, onBlur, onPicked, placeholder }) => (
  <Input
    size="full"
    value={value}
    placeholder={placeholder}
    onBlur={onBlur}
    onChange={(next) => {
      onChange?.(next);
      onPicked?.(next);
    }}
  />
);

/** 发布至：目前仅经分；舆情 / 情报暂隐藏（字段与经分相同、接口不同，后续再开） */
const FormTargetRadio: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
}> = ({ value = 'dashboard', onChange }) => {
  const { formatMessage } = useIntl();
  return (
    <div style={{ verticalAlign: 'middle' }}>
      <Radio
        name="html-publish-target"
        value={value === 'dashboard'}
        onChange={() => onChange?.('dashboard')}
      >
        {formatMessage({
          id: 'dbh_studio_aigallery_report_publish_dashboard',
        })}
      </Radio>
      {/* 舆情 / 情报暂未开放
      <Radio
        name="html-publish-target"
        value={value === 'opinion'}
        onChange={() => onChange?.('opinion')}
      >
        {formatMessage({
          id: 'dbh_studio_aigallery_report_publish_opinion',
        })}
      </Radio>
      <Radio
        name="html-publish-target"
        value={value === 'intelligence'}
        disabled
        onChange={() => onChange?.('intelligence')}
      >
        {formatMessage({
          id: 'dbh_studio_aigallery_report_publish_intelligence',
        })}
      </Radio>
      */}
    </div>
  );
};

/** 标题禁止的半角 / 全角字符（空格允许；中文问号是全角 ？） */
const NAME_FORBIDDEN_CHARS = ['<', '>', '/', '?', '＜', '＞', '／', '？'];

/** 标题校验：1-50，禁止 < > / ?（含全角）；中英文同一套 */
const getNameError = (raw: unknown): boolean => {
  const val = String(raw ?? '').trim();
  if (!val || val.length > 50) {
    return true;
  }
  return some(NAME_FORBIDDEN_CHARS, (ch) => val.includes(ch));
};

const clearFields = (formRef: React.RefObject<ISearchRef>, names: string[]): void => {
  const form = formRef.current?.form;
  forEach(names, (name) => form?.change(name, ''));
};

/**
 * Gallery Report 发布：UseFormSearch 配置；经分走 addMenu，舆情同字段待接接口
 */
export const useHtmlPublishForm = ({
  htmlPath,
  onSuccess,
  formRef,
}: IUseHtmlPublishFormOptions) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const userInfo = useSelector(
    (state: { home?: { userInfo?: { userid?: string; }; }; }) =>
      state.home?.userInfo,
  );
  const submittingRef = useRef(false);

  const { data: gameData } = useRequestController({
    method: 'POST',
    url: '/api/v1/dashboard/userMessage',
    refreshDeps: USER_MESSAGE_BODY,
    cacheKey: 'new-chart-game-list',
    cacheTime: 1800000,
    staleTime: 1800000,
  });
  const gameList = useMemo(
    () => flattenAdminGames(get(gameData, 'main_game')),
    [gameData],
  );

  const [gameCode, setGameCode] = useState('');
  const [menuId, setMenuId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [level, setLevel] = useState('');
  const [nameEN, setNameEN] = useState('');
  const [nameCN, setNameCN] = useState('');
  const [menuTree, setMenuTree] = useState(EMPTY_TREE);
  const selectedGame = find(gameList, { game_code: gameCode });

  useEffect(() => {
    if (!selectedGame?.is_access) {
      setMenuTree(EMPTY_TREE);
      return;
    }
    getLeftMenuList({
      user_id: '101',
      game_code: selectedGame.game_code,
      studio_code: selectedGame.studio,
      role_id: selectedGame.role_id,
      type: selectedGame.role_type,
      third_party_game: selectedGame.third_party_game,
    }).then((result: { menu_list?: Array<Record<string, unknown>>; }) => {
      setMenuTree(parseMenuTree(result?.menu_list));
    });
  }, [selectedGame?.game_code]);

  // 菜单一期已在 parseMenuTree 排除 business_type=game（基础/框架），不再走经分 useFilterMenu
  const filterMenuList = useMemo(() => {
    const nameKey = locale === 'zh-CN' ? 'name_cn' : 'name_en';
    return map(menuTree.menuList, (menu) => ({
      ...menu,
      value: menu.id,
      text: menu[nameKey as 'name_cn' | 'name_en'] || menu.name_en || '',
    }));
  }, [locale, menuTree.menuList]);
  const { filterFolderList } = useFilterFolder({
    folderList: menuTree.folderList,
    menuId,
    locale,
  });
  const { filterFolderList: subFolderOptions } = useFilterFolder({
    folderList: menuTree.subFolderList,
    menuId: folderId,
    locale,
  });
  const { userLevelList } = useListUserLevel({
    currentGame: selectedGame,
    userInfo,
    formatMessage,
    item: undefined,
  });

  const incompleteMsg = formatMessage({
    id: 'dbh_studio_aigallery_report_publish_incomplete',
  });
  // 中英文标题共用同一句报错
  const nameErrorMsg = formatMessage({
    id: 'dbh_studio_aigallery_report_publish_name_en_error',
  });

  const config = useMemo((): IConfig[] => {
    const nameKey = locale === 'zh-CN' ? 'game_cn_name' : 'game_name';
    const gameOptions = map(gameList, (game) => ({
      value: game.game_code,
      text: game[nameKey] || game.game_name || game.game_cn_name || '',
    }));
    return [
      {
        name: 'target',
        label: formatMessage({ id: 'dbh_studio_aigallery_report_publish_to' }),
        className: 'dbh-html-publish-modal__target-item',
        component: <FormTargetRadio />,
        rules: {
          required: true,
          message: incompleteMsg,
          validator: (values: TFormValues) =>
            values.target === 'dashboard' ? '' : incompleteMsg,
        },
      },
      {
        name: 'nameEN',
        label: formatMessage({
          id: 'dbh_studio_aigallery_report_publish_name_en',
        }),
        component: (
          <FormInput
            placeholder={formatMessage({
              id: 'dbh_studio_aigallery_report_publish_name_en_placeholder',
            })}
            onPicked={setNameEN}
          />
        ),
        rules: {
          required: true,
          // 不写静态 message：避免 Form.Item 在 meta.error 空时用兜底文案「粘住」红字
          message: '',
          validator: (values: TFormValues) =>
            getNameError(values.nameEN) ? nameErrorMsg : '',
        },
      },
      {
        name: 'nameCN',
        label: formatMessage({
          id: 'dbh_studio_aigallery_report_publish_name_cn',
        }),
        component: (
          <FormInput
            placeholder={formatMessage({
              id: 'dbh_studio_aigallery_report_publish_name_cn_placeholder',
            })}
            onPicked={setNameCN}
          />
        ),
        rules: {
          required: true,
          message: '',
          validator: (values: TFormValues) =>
            getNameError(values.nameCN) ? nameErrorMsg : '',
        },
      },
      {
        name: 'gameCode',
        label: formatMessage({ id: 'game_selector_tab1' }),
        component: (
          <FormSelect
            searchable
            options={gameOptions}
            onPicked={(next) => {
              setGameCode(next);
              setMenuId('');
              setFolderId('');
              setLevel('');
              clearFields(formRef, ['menu', 'folder', 'subFolder', 'level']);
            }}
          />
        ),
        rules: {
          required: true,
          message: formatMessage({ id: 'menu_select_null' }),
        },
      },
      {
        name: 'menu',
        label: formatMessage({ id: 'menu' }),
        component: (
          <FormSelect
            options={filterMenuList}
            disabled={!gameCode}
            onPicked={(next) => {
              setMenuId(next);
              setFolderId('');
              clearFields(formRef, ['folder', 'subFolder']);
            }}
          />
        ),
        rules: {
          required: true,
          message: formatMessage({ id: 'menu_select_null' }),
          validator: (values: TFormValues) =>
            values.gameCode
              ? values.menu
                ? ''
                : formatMessage({ id: 'menu_select_null' })
              : '',
        },
      },
      {
        name: 'folder',
        label: formatMessage({ id: 'folder' }),
        component: (
          <FormSelect
            options={filterFolderList}
            disabled={!menuId}
            onPicked={(next) => {
              setFolderId(next);
              clearFields(formRef, ['subFolder']);
            }}
          />
        ),
        rules: {
          required: true,
          message: formatMessage({ id: 'menu_folder_select_null' }),
          validator: (values: TFormValues) =>
            values.menu
              ? values.folder
                ? ''
                : formatMessage({ id: 'menu_folder_select_null' })
              : '',
        },
      },
      {
        name: 'subFolder',
        label: formatMessage({ id: 'subfolder' }),
        component: (
          <FormSelect options={subFolderOptions} disabled={!folderId} />
        ),
      },
      {
        name: 'level',
        label: formatMessage({ id: 'menu_level' }),
        component: (
          <FormSelect searchable options={userLevelList} onPicked={setLevel} />
        ),
        rules: {
          required: true,
          message: formatMessage({ id: 'menu_user_level_select_null' }),
        },
      },
    ];
  }, [
    filterFolderList,
    filterMenuList,
    folderId,
    formRef,
    formatMessage,
    gameCode,
    gameList,
    incompleteMsg,
    locale,
    menuId,
    nameErrorMsg,
    subFolderOptions,
    userLevelList,
  ]);

  const handleFormSubmit = (raw: TFormValues): void => {
    const game = find(gameList, { game_code: raw.gameCode });
    if (!game || submittingRef.current) {
      return;
    }
    // 舆情与经分字段相同、接口不同；舆情入口暂隐藏
    if (raw.target !== 'dashboard') {
      return;
    }
    submittingRef.current = true;
    addMenu({
      menu_type: 2,
      level: raw.level,
      u_parent_id: raw.subFolder || raw.folder,
      page_type: PAGE_TYPE_AUTH_PATH,
      name_en: String(raw.nameEN ?? '').trim(),
      name_cn: String(raw.nameCN ?? '').trim(),
      game_code: game.game_code,
      role_type: game.role_type,
      studio_code: game.studio,
      user_id: userInfo?.userid,
      // Auth Path：相对路径写 path，url 置空（对齐经分 FormPath getPathInfo）
      path: htmlPath,
      url: '',
      workbook_url: '',
      dashboard_uuid: '',
      preset_id: '',
    }).then(
      () => {
        notification.success({
          description: formatMessage({
            id: 'dbh_studio_aigallery_report_publish_success',
          }),
        });
        onSuccess();
      },
      (err: { msg?: string; code?: number; }) => {
        notification.error({
          description:
            err?.code === 30011
              ? formatMessage({ id: 'menu_dashboard_re_enter' })
              : err?.msg || '',
        });
      },
    ).finally(() => {
      submittingRef.current = false;
    });
  };

  return {
    config,
    initialValues: {
      target: 'dashboard' as const,
    },
    handleFormSubmit,
    // 经分必填校验；舆情入口暂隐藏
    submitDisabled:
      getNameError(nameEN) ||
      getNameError(nameCN) ||
      !gameCode ||
      !menuId ||
      !folderId ||
      !level,
  };
};
